import { colors } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { styled } from "@mui/material/styles";
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
  AccordionSummaryProps,
  accordionSummaryClasses,
} from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';

export const Container = styled("div", { name: "AppDrawerContainer" })<{ fullHeight?: boolean }>(
  ({ fullHeight }) => ({
    display: "flex",
    height: fullHeight ? "100%" : undefined,
    overflow: "auto",
    position: "relative"
  })
);

export const Main = styled("div")(() => ({
  background: "white",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
}));

export const MainFooter = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  padding: "24px",
  gap: "16px",
  height: "88px",
  backgroundColor: "transparent",
  borderTop: "1px solid #E4E7EC",
}));

export const MaiFooterSubmit = styled("div")(() => ({
  flex: 1,
  display: "flex",
  justifyContent: "flex-end",
}));

export const MainFooterSave = styled("button")(() => ({
  flex: 1,
  backgroundColor: "#0055A4",
  color: "white",
  borderRadius: 8
}));

export const MainTitle = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  padding: "24px",
  gap: "24px",
  height: "88px",
  backgroundColor: "transparent",
  borderBottom: "1px solid #E4E7EC",
  fontFamily: "Manrope",
  fontWeight: 700,
  fontSize: "22px",
  lineHeight: "28px",
  letterSpacing: "0%",
}));

export const MainTitleLabel = styled("div")(() => ({
  flex: 1,
  color: "#475467",
  fontFamily: "Manrope",
  fontSize: "22px",
  fontWeight: 700,
  lineHeight: "28px",
}));

export const TableLabel = styled("div")(() => ({
  color: "#667085",
  fontFamily: "Manrope",
  fontSize: "10px",
  fontWeight: 700,
  lineHeight: "15px",
  letterSpacing: "0.5px",
  textTransform: "uppercase",
}));

export const MainContent = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  padding: "24px",
  overflowY: "auto",
  backgroundColor: "#F9FAFB",
  flex: 1,
}));

export const AccessRuleForm = styled("form")(() => ({
  display: "flex",
  flexDirection: "column",
}));

export const Accordion = styled(MuiAccordion)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));

export const AccordionSummary = styled(MuiAccordionSummary)(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
      transform: 'rotate(90deg)',
    },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1),
  },
  ...theme.applyStyles('dark', {
    backgroundColor: 'rgba(255, 255, 255, .05)',
  }),
}))

export const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

const styles = makeStyles(() => ({
  gridWrapper: {
    "& .shipeezi-new-grid-component": {
      "& > div": {
        padding: "0px !important",
        margin: "0px !important"
      }
    },
    "& .MuiTableCell-head": {
      padding: "10px !important",
      minHeight: "32px",
      maxHeight: "48px",
    },
    "& .MuiTableCell-body": {
      paddingLeft: "16px !important",
      paddingRight: "16px !important",
      minHeight: "40px",
      maxHeight: "64px",
    },
  },
  inputLabel: {
    color: "#D92D20",
  },
  label: {
    fontFamily: "Manrope",
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.1px",
    verticalAlign: "middle",
    color: "#004D95",
    marginBottom: "4px",
  },
  labelDisabled: {
    fontFamily: "Manrope",
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.1px",
    verticalAlign: "middle",
    color: "#98A2B3",
    marginBottom: "4px",
  },
  statusBadge: {
    borderRadius: 100,
    borderWidth: 1,
    gap: 8,
    background: "#E4E7EC !important",
    border: "1px solid #E0E2E7",
    padding: "2px 8px",
    fontWeight: 700,
    fontSize: 11,
    lineHeight: "16px",
    color: "#667085 !important",
    fontFamily: "Manrope",
  },
  statusBadgeFullAccess: {
    border: "1px solid #ECFDF3",
    padding: "2px 8px",
    borderRadius: 20,
    height: 28,
    fontWeight: 600,
    fontSize: 11,
    lineHeight: "20px",
    background: "#ECFDF3 !important",
    color: "#12B76A !important",
  },
  statusBadgePartialAccess: {
    border: "1px solid #FFFAEB",
    padding: "2px 8px",
    borderRadius: 20,
    height: 28,
    fontWeight: 600,
    fontSize: 11,
    lineHeight: "20px",
    background: "#FFFAEB !important",
    color: "#F79009 !important",
  },
  statusBadgeActive: {
    background: "#ECFDF3 !important",
    border: "1px solid #ECFDF3",
    color: "#027A48 !important",
  },
  statusBadgeInactive: {
    background: "#FEF3F2 !important",
    border: "1px solid #FEF3F2",
    color: "#B42318 !important",
  },
  groupInfo: {
    minHeight: "64px",
    maxHeight: "fit-content",
    display: "flex",
    background: "#E6EEF6",
    border: "1px solid #8AB1D5",
    borderRadius: "8px",
    padding: "12px",
  },
  groupInfoIcon: {
    fontSize: "20px",
    color: "#003C74",
  },
  groupInfoContent: {
    flex: 1,
    padding: "0 8px",
    color: "#003C74",
    lineHeight: "20px",
    letterSpacing: "0.25px",
    fontSize: "14px",
    fontWeight: 400,
    fontFamily: "Manrope",
  },
  resourceWrapper: {
    backgroundColor: "#F2F4F7",
    border: "1px solid #F2F4F7",
    borderRadius: "8px",
    boxShadow: "none",
    "& .MuiCardContent-root:last-child": {
      paddingBottom: "0px !important",
    },
  },
  resourceHeader: {
    padding: "8px 16px",
    display: "flex",
    alignItems: "center",
  },
  resourceTitle: {
    flex: 1,
    color: "#667085",
    fontFamily: "Manrope",
    fontWeight: 700,
    fontSize: "16px",
    letterSpacing: "0.5px",
    lineHeight: "24px",
    margin: 0,
    padding: 0,
  },
  resourceExpand: {
    cursor: "pointer",
    color: "#0055A4",
    fontFamily: "Manrope",
    fontWeight: 600,
    fontSize: "12px",
    letterSpacing: "0.5px",
    lineHeight: "16px",
    margin: 0,
    padding: 0,
  },
  resourceContent: {
    padding: "0px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  iconResource: {
    color: "#344054",
    marginRight: "4px",
  },
  accordion: {
    width: "100%",
    boxShadow: "none",
    backgroundColor: "#F2F4F7",
    borderRadius: "0px",
    border: "1px solid #E4E7EC"
  },
  accordionSummary: {
    "& .MuiAccordionSummary-content": {
      marginLeft: "0px !important",
    },
    display: "flex",
    alignItems: "center",
  },
  accordionIconContainer: {
    flex: 1,
  },
  accordionSummaryTitle: {
    fontFamily: "Manrope",
    fontWeight: 700,
    fontSize: "14px",
    letterSpacing: "0.1px",
    lineHeight: "20px",
    color: "#344054"
  },
  accordionSummaryAction: {
    display: "flex",
    alignItems: "center",
  },
  accordionDetails: {
    padding: "8px 16px",
    border: "1px solid #E4E7EC",
    backgroundColor: "#F9FAFB",
    minHeight: "40px",
    borderTopRightRadius: "8px",
    borderTopLeftRadius: "8px",
  },
  accordionDetailsCard: {
    width: "100%",
    borderRadius: "8px",
    boxShadow: "none",
    marginTop: "6px",
  },
  accordionDetailsCardContent: {
    padding: "16px",
    backgroundColor: "#FCFCFD",
  },
  accordionDetailsTitle: {
    fontFamily: "Manrope",
    fontWeight: 700,
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.1px",
    color: "#475467"
  },
  accordionDetailsLabel: {
    wordWrap: "break-word",
    marginBottom: 0
  },
  viewGroup: {
    cursor: "pointer",
    height: "32px",
    width: "fit-content",
    border: "1px solid #0055A4",
    borderRadius: "6px",
    padding: "8px 12px",
    display: "flex",
    alignItems: "center",
  },
  viewGroupTitle: {
    fontFamily: "Manrope",
    fontWeight: 600,
    fontSize: "12px",
    letterSpacing: "0.5px",
    lineHeight: "16px",
    color: "#0055A4",
    textAlign: "center",
  },
  duplicateBtnContainer: {
    "& .MuiButton-root": {
      minWidth: "40px !important",
    }
  },
  duplicateBtn: {
    cursor: "pointer",
    padding: "8px",
    border: "1px solid #475467",
    borderRadius: "8px",
    height: "40px",
    maxWidth: "40px !important",
    "&:hover": {
      border: "1px solid #475467",
      backgroundColor: "transparent"
    }
  },
  deleteBtn: {
    cursor: "pointer",
    textTransform: "capitalize",
    border: "1px solid #F04438",
    borderRadius: "8px",
    color: "#F04438",
    fontFamily: "Manrope",
    fontWeight: 600,
    fontSize: "14px",
    letterSpacing: "0.1px",
    lineHeight: "20px",
    textAlign: "center",
    marginLeft: "14px",
    height: "40px",
    padding: "10px 20px",
    "&:hover": {
      border: "1px solid #F04438",
      backgroundColor: "transparent"
    }
  },
  subFeature: {
    fontFamily: "Manrope",
    fontWeight: 600,
    fontSize: "10px",
    letterSpacing: "0.5px",
    lineHeight: "15px",
    color: "#667085",
    verticalAlign: "middle",
    textTransform: "uppercase",
  },
  permissionText: {
    fontFamily: "Manrope",
    fontWeight: 400,
    fontSize: "12px",
    letterSpacing: "0.25px",
    lineHeight: "20px",
    color: "#667085",
  },
  feaatureFunction: {
    borderBottom: "1px solid #E4E7EC",
    padding: "12px 0px",
    "&:first-child": {
      paddingTop: "0px",
    },
    "&:last-child": {
      borderBottom: "none",
      paddingBottom: "0px",
    },
  },
  checkboxPermissionContainer: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
  },
  accordionResourceCheckbox: {
    fontFamily: "Manrope",
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.25px",
    verticalAlign: "middle",
    color: "#667085"
  },
  accordionPermissionCheckbox: {
    fontFamily: "Manrope",
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "16px",
    letterSpacing: "0.4px",
    verticalAlign: "middle",
    color: "#667085"
  },
  btnClose: {
    fontFamily: "Manrope",
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.1px",
    textAlign: "center",
    color: "#0055A4",
    textTransform: "capitalize",
    height: "40px",
    gap: "8px",
    padding: "10px 12px",
  },
  btnSubmit: {
    cursor: "pointer",
    textTransform: "capitalize", 
    height: "40px",
    gap: "8px",
    padding: "10px 20px",
    fontFamily: "Manrope",
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.1px",
    textAlign: "center",
    borderRadius: "8px"
  },
  selectItem: {
    fontFamily: "Manrope",
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.25px",
    verticalAlign: "middle",
    color: "#344054"
  },
  closeRuleDrawer: {
    cursor: "pointer",
    width: "40px",
    height: "40px",
    padding: "8px",
    gap: "8px"
  },
  overlayLoading: {
    backgroundColor: "rgba(255, 255, 255, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1000
  }
}));

export default styles;
